import React, { FC, SyntheticEvent, useCallback, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Popover,
  PopoverOrigin,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { changeResourceExplorerDateThunk } from "@/store/resource-explorer/thunks/resource-explorer/changeResourceExplorerDateThunk";
import { useAppDispatch } from "@/store/hooks";

interface ResourceExplorerForecastSuggestionPopoverProps {
  open: boolean;
  onClose(): void;
  anchor: HTMLElement | null;
}

type PossibleDateValues = "month_to_date" | "quarter_to_date";

export const ResourceExplorerForecastSuggestionPopover: FC<
  ResourceExplorerForecastSuggestionPopoverProps
> = ({ open, onClose, anchor }) => {
  const dispatch = useAppDispatch();

  const [selectedDate, setSelectedDate] =
    useState<PossibleDateValues>("month_to_date");

  const dateChangeHandler = useCallback((_: SyntheticEvent, value: string) => {
    setSelectedDate(value as PossibleDateValues);
  }, []);

  const applyDateHandler = useCallback(() => {
    dispatch(changeResourceExplorerDateThunk({ label: selectedDate }));
    onClose();
  }, [dispatch, selectedDate, onClose]);

  return (
    <Popover
      open={open}
      anchorEl={anchor}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      slotProps={{
        paper: paperProps,
      }}
    >
      <Stack
        p={1.5}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        borderBottom={1}
        borderColor="grey.300"
      >
        <Typography variant="body2" fontWeight="medium">
          Select a supported date range to view forecasts
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Box p={1.5} borderBottom={1} borderColor="grey.300">
        <Alert severity="info">
          Forecasts are only displayed when the selected date range is Month to
          date or Quarter to date, and the granularity is set to Daily.
        </Alert>
        <Typography variant="body2" pt={1}>
          Please select one of the supported date ranges.
        </Typography>

        <RadioGroup
          row
          value={selectedDate}
          onChange={dateChangeHandler}
          sx={{ px: 1.5 }}
        >
          <FormControlLabel
            value="month_to_date"
            control={<Radio size="small" sx={{ px: 0.5 }} />}
            label="Month to date"
          />
          <FormControlLabel
            value="quarter_to_date"
            control={<Radio size="small" sx={{ p: 0.5 }} />}
            label="Quarter to date"
          />
        </RadioGroup>
      </Box>

      <Stack
        p={1.5}
        spacing={1.5}
        direction="row"
        alignItems="center"
        justifyContent="end"
      >
        <Button onClick={onClose} color="tertiary">
          Cancel
        </Button>
        <Button type="submit" variant="contained" onClick={applyDateHandler}>
          Apply
        </Button>
      </Stack>
    </Popover>
  );
};

const anchorOrigin: PopoverOrigin = {
  vertical: "bottom",
  horizontal: "right",
};
const transformOrigin: PopoverOrigin = {
  vertical: -4,
  horizontal: 300,
};

const paperProps = {
  style: { width: 390 },
};
